import { useState } from 'react'

import { useResetVorschau, useTripReset } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { groesse } from '../../lib/format'
import { useTexte, WORTLAUT } from '../../texte'

/** „1 Nachricht" / „3 Nachrichten" — die Pluralregel steht an einer Stelle. */
function worte(anzahl: number, eins: string, viele: string): string {
  return WORTLAUT.reise.zuruecksetzen.anzahl(anzahl, eins, viele)
}

/**
 * Den Inhalt einer Reise wegwerfen und die Reise behalten (#12).
 *
 * Aus dem Issue: „Eine Reise wird angelegt, kurz zu zweit ausprobiert — und
 * danach soll das Probegeschreibsel weg sein, bevor die eigentlichen Empfänger
 * dazukommen." Es ist ein **Zurücksetzen, kein Löschen**.
 *
 * **Zwei Schritte, und der erste ist keine Zierde.** Erst steht da, was genau
 * verschwindet — gezählt vom Server, nicht geraten —, dann muss ein Code
 * abgetippt werden. Beides zusammen ist die Rückfrage; eine gewöhnliche
 * „Wirklich?"-Frage wäre hier zu wenig, weil es nichts zurückzuholen gibt.
 *
 * **Der Code ist eine Bestätigung, kein Geheimnis.** Wer das hier überhaupt
 * sieht, ist der Inhaber der Reise und dürfte auch ohne. Er soll verhindern,
 * dass jemand seinen Bericht mit einem Fehlgriff wegwischt.
 */
export function Zuruecksetzen({ tripId, titel }: { tripId: number; titel: string }) {
  const texte = useTexte()
  const t = texte.reise.zuruecksetzen
  const [offen, setOffen] = useState(false)
  const [getippt, setGetippt] = useState('')
  const [befund, setBefund] = useState<string | null>(null)
  const vorschau = useResetVorschau(tripId, offen)
  const reset = useTripReset(tripId)
  const online = useOnline()

  const zahlen = vorschau.data
  const passt = zahlen !== undefined && getippt.trim().toUpperCase() === zahlen.code

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.titel}</h2>

      <p className="feld__hinweis klein">{t.was}</p>
      <p className="feld__hinweis klein">
        {t.wofuerVor}
        <strong>{t.wofuerFett}</strong>
      </p>

      {!offen && (
        <button
          type="button"
          className="button button--outline button--gefahr"
          onClick={() => {
            setGetippt('')
            setBefund(null)
            setOffen(true)
          }}
        >
          {t.knopf}
        </button>
      )}

      {offen && (
        <div className="stack stack--luft">
          {vorschau.isPending && <p className="feld__hinweis">{t.wirdGezaehlt}</p>}

          {vorschau.isError && (
            <p className="notice" role="alert">
              {vorschau.error instanceof Error
                ? vorschau.error.message
                : texte.bausteine.uebersichtFehlt}
            </p>
          )}

          {zahlen !== undefined && (
            <>
              {/* **Was verschwindet, in Zahlen.** Erst danach kommt das Feld:
                  wer nicht gelesen hat, was es kostet, soll nicht tippen
                  können. */}
              <div>
                <p className="zwischenzeile">{t.verschwindet}</p>
                <ul className="hinweisliste">
                  <li>{worte(zahlen.messages, t.nachricht, t.nachrichten)}</li>
                  <li>
                    {worte(zahlen.media, t.medium, t.medien)}
                    {zahlen.bytes > 0 && t.mitGroesse(groesse(zahlen.bytes))}
                  </li>
                  <li>{worte(zahlen.tracks, t.gpx, t.gpxMehrere)}</li>
                  <li>{worte(zahlen.highlights, t.punkt, t.punkte)}</li>
                </ul>
              </div>

              {/* **Wer mitliest, gehört gesagt.** Zurücksetzen geht auch mit
                  Empfängern — dann trifft es nicht nur das eigene
                  Probegeschreibsel, sondern auch deren Antworten. */}
              {zahlen.readers > 0 && (
                <p className="notice" role="alert">
                  {worte(zahlen.readers, t.leserEins, t.leserViele)}
                  {t.leserWarnung}
                </p>
              )}

              <label className="field">
                <span className="field__label">
                  {t.abtippenVor}
                  <strong>{zahlen.code}</strong>
                </span>
                <input
                  className="field__input"
                  value={getippt}
                  onChange={(event) => setGetippt(event.target.value)}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label={t.abtippenLabel(zahlen.code)}
                  placeholder={zahlen.code}
                />
              </label>

              {befund !== null && (
                <p className="feld__hinweis" role="status" data-zurueckgesetzt="">
                  {befund}
                </p>
              )}

              {reset.isError && (
                <p className="notice" role="alert">
                  {reset.error instanceof Error
                    ? reset.error.message
                    : texte.allgemein.schiefgelaufen}
                </p>
              )}

              {/* Ohne Netz abgeschaltet, mit Grund als Text — ein Tooltip ist
                  auf dem Telefon unsichtbar. */}
              {!online && (
                <p className="feld__hinweis" role="alert">
                  {texte.allgemein.ohneNetz}
                </p>
              )}

              <div className="row row--eng">
                <button
                  type="button"
                  className="button button--outline button--small"
                  onClick={() => setOffen(false)}
                >
                  {texte.allgemein.abbrechen}
                </button>
                <button
                  type="button"
                  className="button button--outline button--small button--gefahr"
                  disabled={!passt || !online || reset.isPending}
                  onClick={async () => {
                    const weg = await reset.mutateAsync(getippt)
                    setOffen(false)
                    setGetippt('')
                    // **Gesagt wird, was wirklich wegging** -- die Zahlen der
                    // Antwort und nicht die der Vorschau davor.
                    setBefund(
                      t.fertig(
                        titel,
                        worte(weg.messages, t.nachricht, t.nachrichten),
                        worte(weg.media, t.datei, t.dateien),
                        worte(weg.tracks, t.gpx, t.gpxMehrere),
                        worte(weg.highlights, t.punktKurz, t.punkteKurz),
                      ),
                    )
                  }}
                >
                  {reset.isPending ? t.laeuft : t.endgueltig}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Der Befund bleibt stehen, wenn der Kasten wieder zu ist -- sonst
          passierte etwas und niemand sähe es. */}
      {/* Das Merkmal an beiden Stellen: der Satz ist zusammengesetzt, und ein
          Prüflauf käme sonst nur über ein Stück davon heran (1.23.0). */}
      {!offen && befund !== null && (
        <p className="feld__hinweis" role="status" data-zurueckgesetzt="">
          {befund}
        </p>
      )}
    </div>
  )
}
